// WHAT: Individual log entry display component with expandable details and context information
// WHY:  Provides detailed view of single log entry with timestamp, level, domain, scope, and message formatting
// DOMAIN: Logging → Log Display → Entry Rendering
// DEPENDENCIES: LogEntry entity, log formatting utilities, Heroicons
// RISKS:
//   - NOTE: Defensive normalization for mixed log formats - may mask data structure issues
//   - NOTE: Development-only console.debug calls - may impact performance in production
//   - WARN: Message truncation may hide critical error details
//   - NOTE: Expand/collapse state is managed externally - may cause sync issues

import type { LogDomain, LogEntry } from "../../entities";

import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  BugAntIcon,
} from "@heroicons/react/24/outline";
import React from "react";

import {
  formatTimestamp,
  getLogLevelColor,
  getLogDomainColor,
  getLogDomainIcon,
  truncateMessage,
} from "../../utils/logHelpers";

interface LogEntryItemProperties {
  isExpanded: boolean;
  log: LogEntry;
  onToggleExpanded: () => void;
  showContext: boolean;
  showTimestamp: boolean;
}

/**
 * Normalizes domain label from log entry
 *
 * @param log - Log entry to normalize domain from
 *
 *
 * @returns Normalized domain label string
 *
 */
const normalizeDomainLabel = (log: LogEntry): string => {
  if (typeof log.domain === "string") {
    return log.domain;
  }
  if (typeof log.domain === "object") {
    const domainObj = log.domain as {
      domain?: string;
      label?: string;
      name?: string;
    };
    return domainObj.name ?? domainObj.domain ?? domainObj.label ?? "unknown";
  }
  if (typeof log.scope.domain === "string") {
    return log.scope.domain;
  }
  if (typeof log.scope.domain === "object") {
    const scopeDomain = log.scope.domain as {
      domain?: string;
      name?: string;
    };
    return scopeDomain.name ?? scopeDomain.domain ?? "unknown";
  }
  return "unknown";
};

/**
 * Renders expanded details section
 *
 * @param root0 - Component props
 *
 * @param root0.log - Log entry to display details for
 *
 * @param root0.showContext - Whether to show context information
 *
 *
 * @returns React component for expanded log details
 *
 */
const ExpandedDetails: React.FC<{
  log: LogEntry;
  showContext: boolean;
}> = ({ log, showContext }) => {
  return (
    <div className="border-t border-gray-700 bg-gray-750 p-3 space-y-2">
      {/* Full Message */}
      <div>
        <div className="text-xs font-medium text-gray-400 mb-1">Message</div>
        <div className="text-sm text-gray-200 font-mono bg-gray-800 p-2 rounded">{log.message}</div>
      </div>

      {/* Scope Information */}
      <div>
        <div className="text-xs font-medium text-gray-400 mb-1">Scope</div>
        <div className="text-sm text-gray-300">
          <div className="flex items-center space-x-2">
            <span>Domain: {log.scope.domain}</span>
            {log.scope.module ? <span>• Module: {log.scope.module}</span> : null}
            {log.scope.file ? <span>• File: {log.scope.file}</span> : null}
            {log.scope.function ? <span>• Function: {log.scope.function}</span> : null}
            {log.scope.line ? <span>• Line: {log.scope.line}</span> : null}
          </div>
        </div>
      </div>

      {/* Source Information */}
      <div>
        <div className="text-xs font-medium text-gray-400 mb-1">Source</div>
        <div className="text-sm text-gray-300">
          <div className="flex items-center space-x-2">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                log.source === "backend" ? "bg-orange-600 text-white" : "bg-blue-600 text-white"
              }`}
            >
              {log.source === "backend" ? "🦀 Backend" : "⚛️ Frontend"}
            </span>
          </div>
        </div>
      </div>

      {/* Raw Message */}
      <div>
        <div className="text-xs font-medium text-gray-400 mb-1">Raw Message (JSON)</div>
        <div className="text-sm text-gray-300 font-mono bg-gray-800 p-2 rounded max-h-32 overflow-y-auto">
          <pre>{JSON.stringify(log, null, 2)}</pre>
        </div>
      </div>

      {/* Context */}
      {showContext && Object.keys(log.context).length > 0 ? (
        <div>
          <div className="text-xs font-medium text-gray-400 mb-1">Context</div>
          <div className="text-sm text-gray-300 font-mono bg-gray-800 p-2 rounded max-h-32 overflow-y-auto">
            <pre>{JSON.stringify(log.context, null, 2)}</pre>
          </div>
        </div>
      ) : null}

      {/* Additional Info */}
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
        {log.session_id ? (
          <div>
            <span className="font-medium">Session:</span> {log.session_id}
          </div>
        ) : null}
        {log.request_id ? (
          <div>
            <span className="font-medium">Request:</span> {log.request_id}
          </div>
        ) : null}
        {log.component ? (
          <div>
            <span className="font-medium">Component:</span> {log.component}
          </div>
        ) : null}
        {log.duration_ms !== undefined && (
          <div>
            <span className="font-medium">Duration:</span> {log.duration_ms} ms
          </div>
        )}
      </div>

      {/* Stack Trace */}
      {log.stack_trace ? (
        <div>
          <div className="text-xs font-medium text-gray-400 mb-1">Stack Trace</div>
          <div className="text-xs text-gray-300 font-mono bg-gray-800 p-2 rounded max-h-32 overflow-y-auto">
            <pre>{log.stack_trace}</pre>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * WHAT:
 *   Individual log entry display component with expandable details and context information.
 *
 * WHY:
 *   Provides detailed view of single log entry with timestamp, level, domain, scope, and message formatting.
 *
 * PROPS:
 *   - log: LogEntry — Log entry data to display
 *   - isExpanded: boolean — Whether entry is expanded to show full details
 *   - onToggleExpanded: () => void — Callback to toggle expansion state
 *   - showTimestamp: boolean — Whether to display timestamp
 *   - showContext: boolean — Whether to display context data
 *
 * BEHAVIOR:
 *   - Displays log entry with level-based color coding
 *   - Shows domain and scope information
 *   - Truncates long messages with expand/collapse functionality
 *   - Displays timestamp in formatted format
 *   - Shows context data when expanded
 *   - Handles mixed log formats with defensive normalization
 *   - Provides copy-to-clipboard functionality
 *
 * RISKS:
 *   - NOTE: Defensive normalization for mixed log formats - may mask data structure issues
 *   - NOTE: Development-only console.debug calls - may impact performance in production
 *   - WARN: Message truncation may hide critical error details
 *   - NOTE: Expand/collapse state is managed externally - may cause sync issues
 *
 * @param root0
 *
 * @param root0.log
 *
 * @param root0.isExpanded
 *
 * @param root0.onToggleExpanded
 *
 * @param root0.showTimestamp
 *
 * @param root0.showContext
 *
 * @returns React component for log entry item
 *
 */
export const LogEntryItem: React.FC<LogEntryItemProperties> = ({
  isExpanded,
  log,
  onToggleExpanded,
  showContext,
  showTimestamp,
}) => {
  // Defensive normalization for mixed log formats
  const domainLabel = normalizeDomainLabel(log);

  const levelColor = getLogLevelColor(log.level);
  const domainColor = getLogDomainColor(domainLabel as LogDomain);
  const domainIcon = getLogDomainIcon(domainLabel as LogDomain);

  const copyLog = () => {
    const logText = `${formatTimestamp(log.timestamp)} [${log.level.toUpperCase()}] ${domainLabel}: ${log.message}`;
    void navigator.clipboard.writeText(logText);
  };

  const copyLogWithRaw = () => {
    const logText = `${formatTimestamp(log.timestamp)} [${log.level.toUpperCase()}] ${domainLabel}: ${log.message}\n\n${JSON.stringify(log, null, 2)}`;
    void navigator.clipboard.writeText(logText);
  };

  const getLevelIcon = () => {
    switch (log.level) {
      case "error": {
        return <XCircleIcon className="h-4 w-4" />;
      }
      case "warn": {
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      }
      case "info": {
        return <InformationCircleIcon className="h-4 w-4" />;
      }
      case "debug": {
        return <BugAntIcon className="h-4 w-4" />;
      }
      default: {
        return <InformationCircleIcon className="h-4 w-4" />;
      }
    }
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
      {/* Main Log Line */}
      <div
        className="flex items-start space-x-2 p-2 hover:bg-gray-750 transition-colors cursor-pointer"
        onClick={onToggleExpanded}
      >
        {/* Expand/Collapse Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {isExpanded ? (
            <ChevronDownIcon className="h-3 w-3 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 text-gray-400" />
          )}
        </div>

        {/* Level Icon */}
        <div className="flex-shrink-0 mt-0.5" style={{ color: levelColor }}>
          {getLevelIcon()}
        </div>

        {/* Timestamp */}
        {showTimestamp ? (
          <div className="flex-shrink-0 text-xs text-gray-500 mt-0.5">
            {formatTimestamp(log.timestamp)}
          </div>
        ) : null}

        {/* Domain */}
        <div
          className="flex-shrink-0 text-xs font-medium px-1 py-0.5 rounded"
          style={{
            backgroundColor: domainColor + "20",
            color: domainColor,
          }}
        >
          {domainIcon} {domainLabel.toUpperCase()}
        </div>

        {/* Source Badge */}
        <div
          className={`flex-shrink-0 px-1 py-0.5 rounded text-xs font-medium ${
            log.source === "backend" ? "bg-orange-600 text-white" : "bg-blue-600 text-white"
          }`}
        >
          {log.source === "backend" ? "🦀" : "⚛️"}
        </div>

        {/* Message */}
        <div className="flex-1 text-sm text-gray-200 min-w-0">
          {isExpanded ? log.message : truncateMessage(log.message, 80)}
        </div>

        {/* Copy Buttons */}
        <div className="flex items-center space-x-1">
          <button
            className="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              copyLog();
            }}
            title="Log kopieren"
          >
            <DocumentDuplicateIcon className="h-3 w-3" />
          </button>

          <button
            className="flex-shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              copyLogWithRaw();
            }}
            title="Log mit Raw Response kopieren"
          >
            <DocumentDuplicateIcon className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded ? <ExpandedDetails log={log} showContext={showContext} /> : null}
    </div>
  );
};
